import { environment } from "../config/environment";
import { getFromBackendAPI, postToBackendAPI, putToBackendAPI } from "./api.service";


export interface SpreadsheetCreateRequest {
    workbookName: string;
}

export interface SpreadsheetCreateResponse {
    documentId: string;
}

export interface SpreadsheetDto {
    id: string;
    creatorEmail?: string;
    createdAt: string;
}

/**
 * Create a new spreadsheet in the database
 * @param workbookName - Name of the workbook
 * @returns Promise with spreadsheet creation response
 */
export async function createNewSpreadsheet(workbookName: string): Promise<SpreadsheetCreateResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/create`;
  const requestBody: SpreadsheetCreateRequest = {
    workbookName
  };
  return await postToBackendAPI<SpreadsheetCreateResponse>(endpoint, requestBody);
}

/**
 * Get an existing spreadsheet by ID
 * @param id - The spreadsheet ID (GUID)
 * @returns Promise with spreadsheet details
 */
export async function getSpreadsheet(id: string): Promise<SpreadsheetDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${id}`;
  return await getFromBackendAPI<SpreadsheetDto>(endpoint);
}

/**
 * Job-type values accepted by the backend. Storage form is snake_case;
 * human-readable labels live in the Setup page's dropdown options.
 */
export type JobType =
  | "annuals"
  | "periodic"
  | "foodstuffs_annual"
  | "foodstuffs_monthly"
  | "other";

export interface WorkbookSetupRequest {
  clientId?: string;
  clientName?: string;
  balanceDate?: string;
  preparerEmail?: string;
  preparerName?: string;
  reviewerEmail?: string;
  reviewerName?: string;
  xeroTenantId?: string;
  xeroTenantName?: string;
  /** Required on the backend — the Setup page disables save until this is picked. */
  jobType?: JobType;
  /**
   * Per-workbook preference for whether Save (re)writes the WI_Index sheet.
   * The Setup page always sends the current checkbox state; the backend
   * treats an absent value as "don't change".
   */
  createWiIndex?: boolean;
  /**
   * Per-workbook trial-balance period cadence (feature-gated on the Setup
   * page). Mirrors the ValidTbPeriodTypes allow-list on the backend
   * service — add new values in both places together. Absent leaves the
   * stored preference unchanged.
   */
  tbPeriodType?: TrialBalancePeriodType;
  /**
   * Number of PRIOR periods (excluding current) the TB import should
   * cover — passes straight through to the Xero TB endpoint's own
   * periodCount param. The backend caps this PER CADENCE (four years'
   * worth, floored at 5): annuals 5, half-yearly 8, quarterly 16,
   * bi-monthly 24, monthly 48 — see tbPeriodCountMaxFor in Setup.tsx.
   * Absent leaves the stored preference unchanged.
   */
  tbPeriodCount?: number;
}

/**
 * TB period cadence. Kept as a plain string union so this service
 * module has no page-level dependency. Mirrors the backend's
 * ValidTbPeriodTypes; the -to-Xero-endpoint mapping (annuals →
 * "years", quarterly → "quarters", etc.) lives in Setup.tsx.
 */
export type TrialBalancePeriodType =
  | "annuals"
  | "quarterly"
  | "bi-monthly"
  | "half-yearly"
  | "monthly";

export interface WorkbookSetupResponse {
  clientId?: string;
  clientName?: string;
  balanceDate?: string;
  preparerEmail?: string;
  preparerName?: string;
  reviewerEmail?: string;
  reviewerName?: string;
  xeroTenantId?: string;
  xeroTenantName?: string;
  /** Null on legacy workbooks created before the field existed. */
  jobType?: JobType;
  /**
   * Whether Save should (re)write the WI_Index sheet. Column is NOT NULL
   * DEFAULT TRUE at the DB layer, so this is only optional on the wire for
   * interop with the Request shape and any pre-field-migration responses.
   */
  createWiIndex?: boolean;
  /**
   * TB period cadence. Column is NOT NULL DEFAULT 'annuals', so this is
   * optional here only for interop with the Request shape.
   */
  tbPeriodType?: TrialBalancePeriodType;
  /**
   * Number of prior periods. Column is NOT NULL DEFAULT 1 (matches
   * the historical "current + 1 prior" behaviour). Optional here only
   * for interop with the Request shape.
   */
  tbPeriodCount?: number;
}

/**
 * Get workbook setup metadata
 * @param spreadsheetId - The spreadsheet ID (GUID)
 * @returns Promise with workbook setup details
 */
export async function getWorkbookSetup(spreadsheetId: string): Promise<WorkbookSetupResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/setup`;
  return await getFromBackendAPI<WorkbookSetupResponse>(endpoint);
}

/**
 * Save workbook setup metadata
 * @param spreadsheetId - The spreadsheet ID (GUID)
 * @param setup - The setup data to save
 */
export async function saveWorkbookSetup(spreadsheetId: string, setup: WorkbookSetupRequest): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/setup`;
  await putToBackendAPI(endpoint, setup);
}

export interface ChecklistItemResponse {
  index: number;
  text: string;
  response: "yes" | "no";
}

export interface ChecklistResponse {
  items: ChecklistItemResponse[];
}

export interface ReviewPointSnapshotItem {
  discussionId: number;
  title: string;
  status: string;
}

export interface ReviewPointsSnapshot {
  snapshotTime: string;
  reviewPoints: ReviewPointSnapshotItem[];
}

export interface SignRequest {
  checklistResponses: ChecklistItemResponse[];
}

export interface SpreadsheetReviewInfoResponse {
  status: string;
  signingManagerEmail?: string;
  signingManagerTime?: string;
  signingPartnerEmail?: string;
  signingPartnerTime?: string;
  secondarySigningPartnerEmail?: string;
  secondarySigningPartnerTime?: string;
  managerChecklistResponses?: ChecklistResponse;
  partnerChecklistResponses?: ChecklistResponse;
  managerReviewPointsSnapshot?: ReviewPointsSnapshot;
  partnerReviewPointsSnapshot?: ReviewPointsSnapshot;
}

/**
 * Get review/signing info for a spreadsheet
 */
export async function getReviewInfo(spreadsheetId: string): Promise<SpreadsheetReviewInfoResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/review`;
  return await getFromBackendAPI<SpreadsheetReviewInfoResponse>(endpoint);
}

/**
 * Sign the spreadsheet as manager
 */
export async function signAsManager(spreadsheetId: string, request: SignRequest): Promise<SpreadsheetReviewInfoResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/sign-manager`;
  return await postToBackendAPI<SpreadsheetReviewInfoResponse>(endpoint, request);
}

/**
 * Sign the spreadsheet as partner
 */
export async function signAsPartner(spreadsheetId: string, request: SignRequest): Promise<SpreadsheetReviewInfoResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/sign-partner`;
  return await postToBackendAPI<SpreadsheetReviewInfoResponse>(endpoint, request);
}

/**
 * Sign the spreadsheet as secondary partner
 */
export async function signAsSecondaryPartner(spreadsheetId: string): Promise<SpreadsheetReviewInfoResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/sign-secondary-partner`;
  return await postToBackendAPI<SpreadsheetReviewInfoResponse>(endpoint, {});
}